/**
 * Базовые элементы интерфейса.
 *
 * Собраны вручную вместо библиотеки компонентов: их немного, все они
 * простые, а так интерфейс не тянет лишние килобайты и стилизуется
 * теми же переменными тем, что и остальная страница.
 */

import type { ButtonHTMLAttributes, CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { Children, useEffect, useRef, useState, isValidElement } from 'react';
import { tierLabel, type Tier } from '@ak/shared';

type Tone = 'neutral' | 'accent' | 'positive' | 'warning' | 'danger';

const TONE_SOFT: Record<Tone, { background: string; color: string }> = {
  neutral: { background: 'var(--surface-raised)', color: 'var(--text-secondary)' },
  accent: { background: 'var(--accent-soft)', color: 'var(--accent)' },
  positive: { background: 'var(--positive-soft)', color: 'var(--positive)' },
  warning: { background: 'var(--warning-soft)', color: 'var(--warning)' },
  danger: { background: 'var(--danger-soft)', color: 'var(--danger)' },
};

export function Panel({ children, className = '', ...rest }: HTMLAttributes<HTMLDivElement>): ReactNode {
  return (
    <div className={`panel ${className}`} {...rest}>
      {children}
    </div>
  );
}

/** Шапка карточки: заголовок, пояснение и кнопки справа. */
export function PanelHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string | undefined;
  actions?: ReactNode | undefined;
}): ReactNode {
  return (
    <div className="panel-header flex flex-wrap items-center justify-between gap-3 px-5 py-4">
      <div className="min-w-0">
        <h2 className="font-semibold">{title}</h2>
        {description !== undefined && (
          <p className="mt-0.5 text-sm" style={{ color: 'var(--text-muted)' }}>
            {description}
          </p>
        )}
      </div>
      {actions !== undefined && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  // `| undefined` обязателен при exactOptionalPropertyTypes: свойство можно
  // не только опустить, но и передать со значением undefined.
  description?: string | undefined;
  actions?: ReactNode | undefined;
}): ReactNode {
  return (
    <header className="mb-7 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-[1.75rem] leading-tight font-semibold">{title}</h1>
        {description !== undefined && (
          <p className="mt-1.5 text-sm" style={{ color: 'var(--text-muted)' }}>
            {description}
          </p>
        )}
      </div>
      {actions !== undefined && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/**
 * Плитка показателя.
 *
 * Число крупно, подпись мельче, значок в углу — так сводка читается
 * взглядом, а не построчно.
 */
export function StatCard({
  label,
  value,
  hint,
  icon,
  tone = 'accent',
}: {
  label: string;
  value: ReactNode;
  hint?: string | undefined;
  icon?: ReactNode | undefined;
  tone?: Tone;
}): ReactNode {
  const palette = TONE_SOFT[tone];

  return (
    <div className="panel panel-interactive relative overflow-hidden p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
            {label}
          </div>
          <div className="mt-2 text-3xl font-semibold tabular-nums">{value}</div>
        </div>
        {icon !== undefined && (
          <div
            className="grid size-10 shrink-0 place-items-center rounded-xl"
            style={{ background: palette.background, color: palette.color }}
          >
            {icon}
          </div>
        )}
      </div>
      {hint !== undefined && (
        <div className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
          {hint}
        </div>
      )}
    </div>
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  busy?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  busy = false,
  children,
  className = '',
  disabled,
  ...rest
}: ButtonProps): ReactNode {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-xl font-medium ' +
    'transition-[background-color,box-shadow,transform,border-color] duration-150 ' +
    'active:translate-y-px disabled:cursor-not-allowed disabled:opacity-55 disabled:active:translate-y-0';
  const sizing = size === 'sm' ? 'h-8 px-3 text-sm' : 'h-10 px-4 text-sm';

  // Главная кнопка залита градиентом и отбрасывает цветную тень: на странице
  // всегда видно единственное основное действие.
  const styles: Record<string, string> =
    variant === 'primary'
      ? {
          background: 'linear-gradient(135deg, var(--accent), var(--accent-far))',
          color: 'var(--accent-text)',
          border: '1px solid transparent',
          boxShadow: 'var(--shadow-accent)',
        }
      : variant === 'danger'
        ? { background: 'var(--danger-soft)', color: 'var(--danger)', border: '1px solid transparent' }
        : variant === 'ghost'
          ? { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid transparent' }
          : { background: 'var(--surface-raised)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' };

  return (
    <button
      className={`${base} ${sizing} ${variant === 'ghost' ? 'hover:bg-[var(--surface-hover)]' : ''} ${className}`}
      style={styles}
      disabled={disabled === true || busy}
      {...rest}
    >
      {busy && <Spinner size={14} />}
      {children}
    </button>
  );
}

export function Badge({ tone = 'neutral', children, dot = false }: { tone?: Tone; children: ReactNode; dot?: boolean }): ReactNode {
  const palette = TONE_SOFT[tone];

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap"
      style={{ background: palette.background, color: palette.color }}
    >
      {dot && <span className="size-1.5 rounded-full" style={{ background: 'currentColor' }} />}
      {children}
    </span>
  );
}

/** Цвет тира: от горячего первого к спокойному младшему. */
export function tierColor(tier: Tier): string {
  return tier === 0 ? 'var(--tier-none)' : `var(--tier-${String(tier)})`;
}

/**
 * Метка тира.
 *
 * Цвет несёт смысл — чем ниже номер, тем сильнее состав, — но текст
 * дублирует его словами: по одному цвету различать нельзя.
 */
export function TierBadge({ tier }: { tier: Tier }): ReactNode {
  if (tier === 0) {
    return (
      <span className="text-xs whitespace-nowrap" style={{ color: 'var(--tier-none)' }}>
        без тира
      </span>
    );
  }

  const color = tierColor(tier);

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap"
      style={{
        color,
        background: `color-mix(in oklab, ${color} 16%, transparent)`,
        boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${color} 40%, transparent)`,
      }}
    >
      <span className="size-1.5 rounded-full" style={{ background: 'currentColor' }} />
      {tierLabel(tier)}
    </span>
  );
}

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Цвет бегунка для этого варианта. По умолчанию — цвет акцента. */
  color?: string;
}

/**
 * Переключатель вариантов с бегунком, который переезжает к выбранному.
 * Движение показывает, что список один и тот же, просто отбор сменился.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}): ReactNode {
  const index = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const color = options[index]?.color ?? 'var(--accent)';

  return (
    <div
      className="segmented"
      role="group"
      aria-label={label}
      style={
        {
          '--segments': String(options.length),
          '--index': String(index),
          '--segment-color': color,
        } as CSSProperties
      }
    >
      <span className="segmented-thumb" aria-hidden="true" />
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="segmented-option"
          aria-pressed={option.value === value}
          onClick={() => {
            onChange(option.value);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Spinner({ size = 16 }: { size?: number }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className="animate-spin"
      aria-hidden="true"
      role="presentation"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function EmptyState({
  title,
  description,
}: {
  title: string;
  description?: string | undefined;
}): ReactNode {
  return (
    <div className="px-6 py-14 text-center">
      <p className="font-medium">{title}</p>
      {description !== undefined && (
        <p className="mx-auto mt-2 max-w-md text-sm" style={{ color: 'var(--text-muted)' }}>
          {description}
        </p>
      )}
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: (() => void) | undefined;
}): ReactNode {
  return (
    <div className="px-6 py-12 text-center">
      <p className="font-medium" style={{ color: 'var(--danger)' }}>
        {message}
      </p>
      {onRetry !== undefined && (
        <div className="mt-4">
          <Button onClick={onRetry}>Попробовать снова</Button>
        </div>
      )}
    </div>
  );
}

/** Скелет вместо содержимого на время первой загрузки. */
export function Skeleton({ rows = 4 }: { rows?: number }): ReactNode {
  return (
    <div className="space-y-3 p-6" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="h-4 animate-pulse rounded"
          style={{ background: 'var(--surface-hover)', width: `${String(100 - index * 7)}%` }}
        />
      ))}
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string | undefined;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium">{label}</span>
      {children}
      {hint !== undefined && (
        <span className="mt-1.5 block text-xs" style={{ color: 'var(--text-muted)' }}>
          {hint}
        </span>
      )}
    </label>
  );
}

const inputClass =
  'w-full rounded-xl px-3.5 py-2.5 text-sm outline-none transition-all duration-150 ' +
  'border hover:border-[var(--border-strong)] focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)]/30';
const inputStyle: React.CSSProperties = {
  background: 'var(--surface-raised)',
  borderColor: 'var(--border-subtle)',
  color: 'var(--text-primary)',
};

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>): ReactNode {
  const { className = '', style, ...rest } = props;
  return (
    <input
      className={`${inputClass} ${className}`}
      style={{ ...inputStyle, ...style }}
      {...rest}
    />
  );
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>): ReactNode {
  const { className = '', style, ...rest } = props;
  return (
    <textarea
      className={`${inputClass} ${className}`}
      style={{ ...inputStyle, ...style }}
      {...rest}
    />
  );
}

export function Select({
  value,
  onChange,
  children,
  className = '',
  disabled,
}: React.SelectHTMLAttributes<HTMLSelectElement>): ReactNode {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  interface ParsedOption {
    value: string;
    label: string;
    // Явное `| undefined`: при exactOptionalPropertyTypes необязательное
    // поле и поле со значением undefined — разные вещи, а вариант без
    // группы приходит именно вторым.
    group?: string | undefined;
  }

  const parsedOptions: ParsedOption[] = [];
  let currentGroup: string | undefined;

  function extractLabel(node: ReactNode): string {
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(extractLabel).join('');
    if (isValidElement(node)) return extractLabel((node.props as Record<string, unknown>).children as ReactNode);
    return '';
  }

  function walk(nodes: ReactNode): void {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return;
      const type = child.type as unknown;
      const props = child.props as Record<string, unknown>;

      if (type === 'optgroup') {
        const prev = currentGroup;
        currentGroup = props.label as string | undefined;
        walk(props.children as ReactNode);
        currentGroup = prev;
      } else if (typeof props.value === 'string' || typeof props.value === 'number') {
        // Значение приводится к строке только из строки или числа: у объекта
        // это дало бы «[object Object]» в списке вместо внятной подписи.
        parsedOptions.push({
          value: String(props.value),
          label: extractLabel(props.children as ReactNode),
          group: currentGroup,
        });
      }
    });
  }

  walk(children);

  const selected = parsedOptions.find((o) => o.value === String(value ?? ''));

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [open]);

  const fire = (selectedValue: string): void => {
    if (onChange) {
      onChange({ target: { value: selectedValue } } as React.ChangeEvent<HTMLSelectElement>);
    }
  };

  let lastGroup: string | undefined;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        className={`${inputClass} flex items-center justify-between gap-2 text-left disabled:opacity-50 disabled:cursor-not-allowed`}
        style={inputStyle}
        onClick={() => {
          if (!disabled) setOpen(!open);
        }}
      >
        <span className={selected ? '' : 'opacity-40'}>
          {selected?.label ?? String(value ?? '')}
        </span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            color: 'var(--text-muted)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          className="animate-in absolute left-0 z-50 mt-1.5 w-full overflow-hidden rounded-xl border py-1 shadow-lg"
          style={{
            background: 'var(--surface-panel)',
            borderColor: 'var(--border-subtle)',
            boxShadow: 'var(--shadow-raised)',
          }}
        >
          {parsedOptions.map((option) => {
            const groupChanged = option.group !== lastGroup;
            lastGroup = option.group;
            return (
              <span key={option.value}>
                {option.group !== undefined && groupChanged && (
                  <div
                    className="px-3.5 pt-2 pb-1 text-[0.625rem] font-semibold uppercase tracking-[0.08em]"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {option.group}
                  </div>
                )}
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3.5 py-2.5 text-sm transition-colors"
                  style={{
                    background: option.value === String(value ?? '') ? 'var(--accent-soft)' : 'transparent',
                    color: option.value === String(value ?? '') ? 'var(--accent)' : 'var(--text-primary)',
                  }}
                  onMouseEnter={(e) => {
                    if (option.value !== String(value ?? '')) {
                      (e.target as HTMLElement).style.background = 'var(--surface-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (option.value !== String(value ?? '')) {
                      (e.target as HTMLElement).style.background = 'transparent';
                    }
                  }}
                  onClick={() => {
                    fire(option.value);
                    setOpen(false);
                  }}
                >
                  <span className="flex-1 text-left">{option.label}</span>
                  {option.value === String(value ?? '') && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--accent)' }}>
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

const checkboxStyle: React.CSSProperties = {
  width: 20,
  height: 20,
  borderRadius: 6,
  border: '1.5px solid var(--border-strong)',
  background: 'var(--surface-raised)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  cursor: 'pointer',
  transition: 'all 0.15s',
};

const checkboxCheckedStyle: React.CSSProperties = {
  ...checkboxStyle,
  background: 'var(--accent)',
  borderColor: 'var(--accent)',
};

/**
 * Кнопка-переключатель в панели отбора.
 *
 * Набрана теми же отступами и скруглением, что и поле ввода: в панели они
 * стоят в одном ряду, и кнопка ростом с полполя выглядела бы сбоем вёрстки.
 * Ширину задаёт обёртка — так кнопки встают колонками наравне с полями.
 *
 * Цвет приходит снаружи: им отбирают и тир, и исход капта, и сторону, а цвет
 * у каждого свой.
 */
export function FilterChip({
  label,
  color = 'var(--accent)',
  active,
  onSelect,
  title,
  badge,
}: {
  label: string;
  color?: string;
  active: boolean;
  onSelect: () => void;
  title?: string | undefined;
  /** Число рядом с подписью: сколько всего скрывается за этой кнопкой. */
  badge?: number | undefined;
}): ReactNode {
  return (
    <button
      type="button"
      aria-pressed={active}
      title={title}
      className="flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-xl border px-3.5 py-2.5 text-sm font-medium whitespace-nowrap transition-all duration-150"
      style={{
        color: active ? color : 'var(--text-secondary)',
        background: active ? `color-mix(in oklab, ${color} 16%, transparent)` : 'var(--surface-raised)',
        borderColor: active ? `color-mix(in oklab, ${color} 45%, transparent)` : 'var(--border-subtle)',
      }}
      onClick={onSelect}
    >
      {label}
      {badge !== undefined && badge > 0 && (
        <span
          className="rounded-full px-1.5 text-xs"
          style={{ background: `color-mix(in oklab, ${color} 22%, transparent)`, color }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

export function Checkbox({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}): ReactNode {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      style={checked ? checkboxCheckedStyle : checkboxStyle}
      className="transition-all hover:border-[var(--accent)] disabled:opacity-40 disabled:cursor-not-allowed"
      onClick={() => {
        if (!disabled) onChange(!checked);
      }}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          if (!disabled) onChange(!checked);
        }
      }}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      )}
    </button>
  );
}

/** Модальное окно. Закрывается по Escape и по клику вне карточки. */
export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode | undefined;
}): ReactNode {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 backdrop-blur-sm"
      style={{ background: 'oklch(0% 0 0 / 0.5)' }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          onClose();
        }
      }}
      role="presentation"
    >
      <div
        className="panel animate-in w-full max-w-lg"
        style={{ boxShadow: 'var(--shadow-raised)' }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="panel-header flex items-center justify-between px-5 py-4">
          <h2 className="font-semibold">{title}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Закрыть">
            ✕
          </Button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer !== undefined && (
          <div
            className="flex justify-end gap-2 border-t px-5 py-4"
            style={{ borderColor: 'var(--border-subtle)' }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
